// Telegram Mini App detection + auth helpers.
//
// Inside Telegram WebView, regular Supabase email/password auth fails because
// the browser sandbox isolates cookies/storage and blocks OAuth redirects.
// Instead, Telegram passes a signed `initData` payload; we forward it to
// `/auth/telegram-miniapp`, which validates the HMAC against the bot token
// and returns a Supabase magic-link `token_hash`. We then call
// `supabase.auth.verifyOtp` with that hash to mint a real session locally.

import { supabase } from './supabase';
import config from './config';

declare global {
  interface Window {
    Telegram?: {
      WebApp?: {
        initData: string;
        initDataUnsafe: {
          user?: {
            id: number;
            first_name: string;
            last_name?: string;
            username?: string;
            photo_url?: string;
          };
        };
        ready: () => void;
        expand: () => void;
        close: () => void;
        colorScheme?: 'light' | 'dark';
        themeParams?: Record<string, string>;
      };
    };
  }
}

export function isTelegramWebApp(): boolean {
  return typeof window !== 'undefined' && !!window.Telegram?.WebApp?.initData;
}

export function getInitData(): string {
  return window.Telegram?.WebApp?.initData || '';
}

export function readyTelegramWebApp(): void {
  try {
    window.Telegram?.WebApp?.ready();
    window.Telegram?.WebApp?.expand();
  } catch {
    /* not in Telegram */
  }
}

interface MiniAppAuthResponse {
  status?: string;
  action?: string;
  userId?: string;
  email?: string;
  hashed_token?: string;
  action_link?: string;
  email_otp?: string;
  properties?: Record<string, string | undefined>;
}

/**
 * Extract `token` query param from a Supabase action_link URL.
 * Used as a fallback when the API response doesn't carry hashed_token
 * directly but does carry the action_link.
 */
function extractTokenFromActionLink(actionLink: string | undefined): {
  token_hash?: string;
  type?: string;
} {
  if (!actionLink) return {};
  try {
    const u = new URL(actionLink);
    return {
      token_hash: u.searchParams.get('token') || undefined,
      type: u.searchParams.get('type') || undefined,
    };
  } catch {
    return {};
  }
}

/**
 * Чем закончился вход через Телеграм.
 *
 * `not_registered` — человека нет среди сотрудников. Это НЕ поломка: сервер
 * намеренно отказывает вместо того, чтобы завести постороннему пустую
 * учётную запись, в которой всё равно нельзя работать. Экран для такого
 * случая должен быть отдельным и понятным, а не «что-то пошло не так».
 */
export type TwaAuthResult =
  | { ok: true }
  | { ok: false; reason: 'not_registered' | 'error'; telegram?: string | null };

/**
 * Exchange Telegram initData → Supabase session.
 */
export async function authenticateTelegramMiniApp(): Promise<TwaAuthResult> {
  const initData = getInitData();
  if (!initData) {
    console.error('[TWA-auth] no initData — not inside Telegram?');
    return { ok: false, reason: 'error' };
  }

  try {
    const apiBase = config.ASSISTENTUS_API_URL.replace(/\/$/, '');
    const res = await fetch(`${apiBase}/auth/telegram-miniapp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ initData }),
    });

    if (!res.ok) {
      const body = await res.text().catch(() => '');
      console.error('[TWA-auth] backend rejected initData:', res.status, body);
      if (res.status === 403) {
        let telegram: string | null = null;
        try { telegram = JSON.parse(body)?.detail?.telegram ?? null; } catch { /* тело не разобралось */ }
        return { ok: false, reason: 'not_registered', telegram };
      }
      return { ok: false, reason: 'error' };
    }

    const data = (await res.json()) as MiniAppAuthResponse;
    console.log('[TWA-auth] backend response keys:', Object.keys(data), 'props keys:', data.properties && Object.keys(data.properties));

    // Try multiple shapes: flat → properties → action_link query param.
    const tokenHash =
      data.hashed_token ||
      data.properties?.hashed_token ||
      extractTokenFromActionLink(data.action_link || data.properties?.action_link).token_hash;

    const email = data.email || (data.properties && (data.properties as Record<string, string>).email);

    if (!tokenHash) {
      console.error('[TWA-auth] no token in response shape:', data);
      return { ok: false, reason: 'error' };
    }

    // Primary path: verifyOtp with token_hash.
    let { error } = await supabase.auth.verifyOtp({
      type: 'magiclink',
      token_hash: tokenHash,
    });

    // Fallback for old gotrue versions where token_hash flow isn't recognised:
    // use the 6-digit email_otp + email pair.
    if (error && data.email_otp && email) {
      console.warn('[TWA-auth] token_hash flow failed, falling back to email_otp:', error.message);
      const second = await supabase.auth.verifyOtp({
        type: 'email',
        email,
        token: data.email_otp,
      });
      error = second.error;
    }

    if (error) {
      console.error('[TWA-auth] verifyOtp failed:', error);
      return { ok: false, reason: 'error' };
    }
    return { ok: true };
  } catch (err) {
    console.error('[TWA-auth] unexpected error:', err);
    return { ok: false, reason: 'error' };
  }
}
